import { formatDtoolsMoney } from "@/lib/dtoolsJsonFormat";
import { cn } from "@/lib/utils";
import ProposalSystemSummaryTab from "@/components/proposal/ProposalSystemSummaryTab";
import type { ConsolidatedSystemSummaryRow } from "@/components/proposal/ProposalConsolidatedSystemSummaryTable";
import type { DtoolsDisplaySettings } from "@shared/dtoolsDisplaySettings";
import { isDtoolsInternalImportSystem } from "@shared/dtoolsInternalSystems";
import {
  buildDtoolsReviewProposalTotals,
  buildDtoolsReviewSystemView,
  resolveSystemDisplayLabel,
  type DtoolsReviewBuildContext,
} from "@shared/dtoolsReviewNavigation";
import type { DtoolsProposalPresentation } from "@shared/dtoolsProposalPresentation";
import {
  enrichProposalCustomerPricingWithSnapshot,
  scaleProposalCustomerPricingForSystemTotal,
  type ProposalCustomerPricingDisplay,
} from "@shared/proposalCustomerPricing";
import type { DtoolsReviewFooter, DtoolsReviewSystem } from "@shared/dtoolsStagingTransform";
import type { DtoolsV2CustomerProposalView } from "@shared/dtoolsV2CustomerProposalView";
import { proposalShowsRetailPriceDisclaimer } from "@shared/proposalRetailPriceDisclaimer";
import {
  buildInternalSystemSummaryRows,
  buildV2SystemSummaryTabContent,
  collectVisibleSystemsFromView,
} from "@shared/dtoolsSystemSummary";
import type { DtoolsOptionSectionActionsProps } from "@/components/sales/dtools/DtoolsOptionSectionActions";

type Props = {
  view: DtoolsV2CustomerProposalView;
  /** Full hierarchy (incl. internal placeholder systems) so priced Unassigned lines are summarised. */
  systems?: DtoolsReviewSystem[];
  footer: DtoolsReviewFooter;
  presentation?: DtoolsProposalPresentation;
  displaySettings?: DtoolsDisplaySettings;
  reviewContext?: DtoolsReviewBuildContext;
  audience?: "customer" | "staff";
  customerPricing?: ProposalCustomerPricingDisplay | null;
  optionActions?: Omit<DtoolsOptionSectionActionsProps, "optionLabel">;
  className?: string;
};

function buildV2SummarySystemRows(
  view: DtoolsV2CustomerProposalView,
  reviewContext?: DtoolsReviewBuildContext,
  pricing?: ProposalCustomerPricingDisplay | null,
  systems?: DtoolsReviewSystem[],
  displaySettings?: DtoolsDisplaySettings,
): ConsolidatedSystemSummaryRow[] {
  const rows: ConsolidatedSystemSummaryRow[] = [];

  for (const section of view.independentSections) {
    if (isDtoolsInternalImportSystem(section.system)) continue;
    const systemView = buildDtoolsReviewSystemView(section.system, reviewContext, section.hierarchyIndex);
    const amount = systemView.overview.systemTotal;
    rows.push({
      key: `amount-${section.sectionKey}`,
      label: section.title,
      amount,
      customerPricing: pricing ? scaleProposalCustomerPricingForSystemTotal(pricing, amount) : undefined,
    });
  }

  for (const group of view.alternativeGroups) {
    const first = group.options[0];
    if (!first) continue;
    if (group.options.length === 1) {
      if (isDtoolsInternalImportSystem(first.system)) continue;
      const systemView = buildDtoolsReviewSystemView(first.system, reviewContext, first.hierarchyIndex);
      const amount = systemView.overview.systemTotal;
      rows.push({
        key: `amount-group-${group.groupKey}`,
        label: group.groupName,
        amount,
        customerPricing: pricing ? scaleProposalCustomerPricingForSystemTotal(pricing, amount) : undefined,
      });
      continue;
    }
    for (const opt of group.options) {
      if (isDtoolsInternalImportSystem(opt.system)) continue;
      const systemView = buildDtoolsReviewSystemView(opt.system, reviewContext, opt.hierarchyIndex);
      const label = resolveSystemDisplayLabel(opt.system, reviewContext, opt.hierarchyIndex);
      const amount = systemView.overview.systemTotal;
      rows.push({
        key: `amount-group-${group.groupKey}-${opt.optionKey}`,
        label,
        amount,
        customerPricing: pricing ? scaleProposalCustomerPricingForSystemTotal(pricing, amount) : undefined,
      });
    }
  }

  // Priced lines parked in "Unassigned" are always billable: show them as Services / Other items.
  for (const row of buildInternalSystemSummaryRows(systems, displaySettings)) {
    rows.push({
      key: `amount-${row.key}`,
      label: row.label,
      amount: row.amount,
      customerPricing: pricing ? scaleProposalCustomerPricingForSystemTotal(pricing, row.amount) : undefined,
    });
  }

  return rows;
}

export default function DtoolsV2SystemSummaryTab({
  view,
  systems,
  footer,
  presentation,
  displaySettings = {},
  reviewContext,
  customerPricing,
  optionActions,
  className,
}: Props) {
  const content = buildV2SystemSummaryTabContent({
    view,
    systems,
    footerAdjustments: footer.adjustments,
    displaySettings,
    reviewContext,
    formatMoney: formatDtoolsMoney,
  });

  const effectivePricing =
    customerPricing != null
      ? enrichProposalCustomerPricingWithSnapshot(customerPricing, content.snapshot.proposalTotal)
      : null;

  const consolidatedSystemRows = buildV2SummarySystemRows(
    view,
    reviewContext,
    effectivePricing,
    systems,
    displaySettings,
  );

  const adjustmentsTotal = buildDtoolsReviewProposalTotals({
    systems: collectVisibleSystemsFromView(view),
    footerAdjustments: footer.adjustments,
  }).adjustmentsTotal;

  return (
    <ProposalSystemSummaryTab
      content={content}
      presentation={presentation}
      displaySettings={displaySettings}
      consolidatedSystemRows={consolidatedSystemRows}
      proposalPricing={effectivePricing}
      allowIndividualSystemApproval={displaySettings.allowIndividualSystemApproval === true}
      hideCombinedGrandTotal={displaySettings.alternativeOptions === true}
      optionActions={optionActions}
      showRetailPriceDisclaimer={proposalShowsRetailPriceDisclaimer(effectivePricing, adjustmentsTotal)}
      className={cn("dtools-v2-system-summary", className)}
      testId="dtools-v2-system-summary-tab"
    />
  );
}
